import React, { useMemo, useState } from "react";
import SearchField from "@/components/filters/SearchField";
import FilterDropdown from "@/components/filters/FilterDropdown";
import FilterMenuItem from "@/components/filters/FilterMenuItem";
import FilterMenuPanel from "@/components/filters/FilterMenuPanel";
import {
  DATE_SORT_OPTIONS,
  filterShipments,
  formatStatusFilterLabel,
  getShipmentStatusOptions,
  type ShipmentDateSort,
} from "./filterShipments";
import { cn } from "@/lib/utils";

export function useShipmentListFilters(shipments: any[]) {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [dateSort, setDateSort] = useState<ShipmentDateSort>("newest");

  const statusOptions = useMemo(() => getShipmentStatusOptions(shipments), [shipments]);

  const filtered = useMemo(
    () => filterShipments(shipments, { search, status, dateSort }),
    [shipments, search, status, dateSort],
  );

  return {
    search,
    setSearch,
    status,
    setStatus,
    dateSort,
    setDateSort,
    statusOptions,
    filtered,
  };
}

export function ShipmentListFilterBar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  dateSort,
  onDateSortChange,
  statusOptions,
  searchInputClassName = "",
  className = "",
}: {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  dateSort: ShipmentDateSort;
  onDateSortChange: (value: ShipmentDateSort) => void;
  statusOptions: string[];
  searchInputClassName?: string;
  className?: string;
}) {
  const statusLabel = status === "all" ? "Status" : formatStatusFilterLabel(status);
  const dateLabel = DATE_SORT_OPTIONS.find((o) => o.value === dateSort)?.label ?? "Date";

  return (
    <div className={cn("flex items-stretch gap-2", className)}>
      <SearchField
        size="docs"
        placeholder="VIN, Make, Model, Year"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        onClear={() => onSearchChange("")}
        clearable
        className={cn("min-w-0 flex-1", searchInputClassName)}
      />

      <FilterDropdown
        label="Status"
        valueLabel={statusLabel}
        active={status !== "all"}
        menuWidth={168}
      >
        {(close) => (
          <FilterMenuPanel width={168}>
            {statusOptions.map((option) => (
              <FilterMenuItem
                key={option}
                active={status === option}
                onClick={() => {
                  onStatusChange(option);
                  close();
                }}
              >
                {option === "all" ? "All statuses" : formatStatusFilterLabel(option)}
              </FilterMenuItem>
            ))}
          </FilterMenuPanel>
        )}
      </FilterDropdown>

      <FilterDropdown
        label="Date"
        valueLabel={dateSort === "newest" ? "Date" : dateLabel}
        active={dateSort !== "newest"}
        menuWidth={168}
      >
        {(close) => (
          <FilterMenuPanel width={168}>
            {DATE_SORT_OPTIONS.map((option) => (
              <FilterMenuItem
                key={option.value}
                active={dateSort === option.value}
                onClick={() => {
                  onDateSortChange(option.value);
                  close();
                }}
              >
                {option.label}
              </FilterMenuItem>
            ))}
          </FilterMenuPanel>
        )}
      </FilterDropdown>
    </div>
  );
}
